import { FC, useEffect, useState } from "react";
import { getAcceptableLocationTypes, isKeyLocation, KRT, LocationFieldsProps } from "../LocationFields";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { MovementRuleAction, AssetLocation, AssetLocationTypes, KeyManagementTypes, LaneAllocationSystem } from "../../../../logic/api";
import { GridLegacy as Grid } from "@mui/material";
import { formatCamel, isSysAdmin } from "../../../../utility/utils";
import { IDropDownListItem } from "../../../../components/editable/EditableField/fields/DropDownField";
import EditableField, { EditableFieldTypes } from "../../../../components/editable/EditableField/EditableField";
import { LaneColorPicker } from "../LaneColourPicker";
import { usePreferences } from "../../../../utility/usePreferences";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { formatLatLngInput, parseLatLngInput } from "../../../../utility/geoLocation";

export const LaneLocationFields: FC<LocationFieldsProps> = ({ session, location, parentLocation, onGpsLocationValidityChange }) => {
  const keyLocationCreationEnabled = useReleaseManagement(ReleaseFlag.reinstateKeyLocationCreation);
  const vehicleMoveKmosRulesEnabled = useReleaseManagement(ReleaseFlag.vehicleMoveKmosRules);
  const isCarWashLocationEnabled = useReleaseManagement(ReleaseFlag.releaseCarWashLocation);
  const isVipLocationEnabled = useReleaseManagement(ReleaseFlag.releaseVipLocation);
  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);
  const isKeyMasterTimeSlotEnabled = useReleaseManagement(ReleaseFlag.releaseKeyMasterTimeSlot);
  const isSydneyDualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isExtraAllocatableLocationsReleased = useReleaseManagement(ReleaseFlag.releaseExtraAllocatableLocations);
  const isRbacLocationsManagementReleased = useReleaseManagement(ReleaseFlag.releaseRbacLocationsManagement);
  const isMeetAndGreetTypeEnabled = useReleaseManagement(ReleaseFlag.releaseLocationMeetAndGreetType);
  const isGpsLocationReleased = useReleaseManagement(ReleaseFlag.releaseAddGpsCoordinatesToLocation);
  const isCustomerFacingZoneNameEnabled = useReleaseManagement(ReleaseFlag.releaseCustomerFacingZoneName);
  const isLocationKeyManagementDropdownFixReleased = useReleaseManagement(ReleaseFlag.releaseLocationKeyManagementDropdownFix);

  const { kmosManualAllocationEnabled, allocationSystem } = usePreferences();
  const { hasPermission } = useUserPermissions();

  const [isAllocatableReadOnly, setIsAllocatableReadOnly] = useState(false);
  const [gpsLocationText, setGpsLocationText] = useState<string | undefined>(undefined);
  // TODO: DD - Temp fix to show validation on blur, we need to refactor EditableField to handle onBlur internally
  const [showGpsLocationValidation, setShowGpsLocationValidation] = useState(false);

  const isKmosManualAllocationEnabled =
    kmosManualAllocationReleased &&
    kmosManualAllocationEnabled === true;
  const isKmosManualAllocationEnabledV2 =
    isSydneyDualParkAllocationReleased &&
    session.current.site?.preferences.lane_allocation_system === LaneAllocationSystem.KMOS;
  const isKmosEnabled = isSydneyDualParkAllocationReleased
    ? isKmosManualAllocationEnabledV2
    : isKmosManualAllocationEnabled;

  const acceptableLocationTypes = getAcceptableLocationTypes(keyLocationCreationEnabled, parentLocation);

  const parentHasKeyType = Boolean(parentLocation?.parentHasKeyType) || (parentLocation != null && parentLocation.keyType !== KeyManagementTypes.inherit);
  const parentControlsKeyManagement = !isLocationKeyManagementDropdownFixReleased && parentHasKeyType;
  const showLocationTypeField = isLocationKeyManagementDropdownFixReleased && (!location.id || !isRbacLocationsManagementReleased);

  const { t } = useTranslation();

  const isGpsFieldVisible = isGpsLocationReleased && [AssetLocationTypes.Lane, AssetLocationTypes.Zone].includes(location.type);

  useEffect(() => {
    if (!isGpsFieldVisible) {
      onGpsLocationValidityChange?.(true);
    }
  }, [isGpsFieldVisible, onGpsLocationValidityChange]);

  return (<Grid container>
    <EditableField<AssetLocation, string>
      testId="lane-location-name-field"
      inputType={EditableFieldTypes.text}
      label={t("input.labels.name")}
      onProvide={(data) => data.name}
      onChange={(data, value) => data.name = value}
    />

    {isCustomerFacingZoneNameEnabled && location.type === AssetLocationTypes.Zone && (
      <EditableField<AssetLocation, string>
        testId="lane-location-customer-facing-name-field"
        inputType={EditableFieldTypes.text}
        label={t("input.labels.customerFacingName")}
        onProvide={(data) => data.customerFacingName ?? ""}
        onChange={(data, value) => data.customerFacingName = value}
      />
    )}

    {isGpsFieldVisible && (
      <EditableField<AssetLocation, string>
        testId="lane-location-gps-location-field"
        inputType={EditableFieldTypes.text}
        label={t("input.labels.gpsLocation")}
        placeholder={t("input.placeholders.gpsLocation")}
        onProvide={(data) => gpsLocationText ?? formatLatLngInput(data.gpsLocation)}
        onValidate={(_data, value) => (
          value && showGpsLocationValidation && !parseLatLngInput(value).valid
            ? t("input.validations.invalidGpsLocationFormat")
            : null
        )}
        onBlur={() => {
          setShowGpsLocationValidation(true);
          onGpsLocationValidityChange?.(
            !gpsLocationText || parseLatLngInput(gpsLocationText).valid
          );
        }}
        onChange={(data, value) => {
          setGpsLocationText(value);
          setShowGpsLocationValidation(false);
          onGpsLocationValidityChange?.(true);

          const parsed = parseLatLngInput(value);
          if (parsed.valid) {
            data.gpsLocation = parsed.point;
          }
        }}
      />
    )}

    {[AssetLocationTypes.CarPark].includes(location.type) && isSysAdmin(session) && (
      <EditableField<AssetLocation, string>
        testId="lane-location-site-code-field"
        inputType={EditableFieldTypes.text}
        label={t("input.labels.siteCode")}
        onProvide={(data) => data.code}
        onChange={(data, value) => data.code = value}
      />
    )}

    {showLocationTypeField && (
      <EditableField<AssetLocation, IDropDownListItem>
        testId="lane-location-type-field"
        inputType={EditableFieldTypes.terminalField}
        alwaysVisible={!isRbacLocationsManagementReleased}
        readOnly={!!location.id}
        label={t("locations.type")}
        onProvide={(data) => {
          const typeName = data.type != null
            ? formatCamel(AssetLocationTypes[data.type]?.toString() ?? "")
            : "";
          return {
            name: typeName,
            value: data.type == null ? "" : data.type.toString()
          };
        }}
        onChange={(data, item) => {
          data.type = parseInt((item.value ?? "").toString());
        }}
        onRequestData={async () => {

          const items: IDropDownListItem[] = [];
          for (const pt in AssetLocationTypes) {
            const intVal = parseInt(pt);
            if (isNaN(intVal)) {
              if (acceptableLocationTypes.indexOf(parseInt(AssetLocationTypes[pt])) != -1) {

                items.push({ name: formatCamel(pt.toString()), value: AssetLocationTypes[pt] });
              }

            }
          }
          return items;
        }}
      />
    )}

    {([AssetLocationTypes.Lane, AssetLocationTypes.Zone].includes(location.type)) && (
      <EditableField<AssetLocation, IDropDownListItem>
        key={`${isLocationKeyManagementDropdownFixReleased}-${location.id ?? "new"}-${location.type}`}
        testId="lane-location-key-management-field"
        inputType={EditableFieldTypes.terminalField}
        alwaysVisible={!isRbacLocationsManagementReleased}
        readOnly={parentControlsKeyManagement}
        label={t("input.labels.keyManagement")}

        onProvide={(data) => {
          let keyTypeName = "";
          if (isRbacLocationsManagementReleased && data.keyType != null) {
            // TODO: DD - Temporary: display inherit as None
            if (
              data.keyType == KeyManagementTypes.inherit &&
              (isLocationKeyManagementDropdownFixReleased || data.type == AssetLocationTypes.Zone)
            ) {
              keyTypeName = "None";
            } else {
              keyTypeName = formatCamel(KeyManagementTypes[data.keyType]?.toString() ?? "");
            }
          }
          return {
            name: keyTypeName,
            value: data.keyType == null ? "" : data.keyType.toString()
          };
        }}
        onChange={(data, item) => {
          data.keyType = parseInt((item.value ?? "").toString());
        }}
        onRequestData={async (data) => {
          const items: IDropDownListItem[] = [];
          for (const pt in KeyManagementTypes) {
            const intVal = parseInt(pt);
            if (isNaN(intVal)) {

              // TODO: DD - Temporary: display inherit as None
              const isInheritKeyType = KeyManagementTypes[pt] == KeyManagementTypes.inherit.toString();
              if (isInheritKeyType && (isLocationKeyManagementDropdownFixReleased || data.type == AssetLocationTypes.Zone)) {
                items.push({ name: "None", value: KeyManagementTypes[pt] });
              } else {
                items.push({ name: formatCamel(pt.toString()), value: KeyManagementTypes[pt] });
              }


            }
          }
          return items;
        }}
      />
    )}

    {(isKmosEnabled && isKeyMasterTimeSlotEnabled && [AssetLocationTypes.Zone].includes(location.type)) && (
      <>
        <LaneColorPicker
          initialColor={location.color || "#e5e5e5"}
          onChange={(color) => {
            location.color = color;
          }}
          readOnly={!hasPermission("assetTracking.location.manage")}
        />
      </>
    )}

    {[
      AssetLocationTypes.Lane,
      AssetLocationTypes.Hook,
      AssetLocationTypes.DynamicBox,
      AssetLocationTypes.KeyBox,
      AssetLocationTypes.KeyLockerBox
    ].includes(location.type) && (
        <EditableField<AssetLocation, number>
          testId="lane-location-capacity-field"
          inputType={EditableFieldTypes.number}
          label={t("kmos.labels.capacity")}
          onProvide={(data) => data.capacity}
          onChange={(data, value) => data.capacity = value}
        />
      )}

    {[AssetLocationTypes.Zone].includes(location.type) && (
      <>
        <div style={{ "width": "100%", marginTop: 20, marginBottom: 20 }}>
          <b>{t("locations.zoneOptions")}</b>
        </div>

        <EditableField<AssetLocation, boolean>
          testId="lane-location-self-park-switch"
          inputType={EditableFieldTypes.switch}
          label={t("booking.labels.selfPark")}
          onProvide={(data) => data.selfPark}
          onChange={(data, value) => {
            data.selfPark = value;
            if (value == true) {
              data.returns = false;
            }
          }}
        />
      </>
    )}

    {([AssetLocationTypes.Lane].includes(location.type) || (
      isKeyLocation(location.type)
    )) && (
        <>
          <div style={{ "width": "100%", marginTop: 20, marginBottom: 20 }}>
            <b>{t("locations.laneOptions")}</b>
          </div>
          <EditableField<AssetLocation, boolean>
            testId="lane-location-returns-switch"
            inputType={EditableFieldTypes.switch}
            label={t("input.labels.returns")}
            onProvide={(data) => data.returns}
            onChange={(data, value) => {
              data.returns = value;
              if (value == true) {
                data.blockPark = false;

                // When Block Park is set to false, re-enable the isAllocatable switch
                if (isExtraAllocatableLocationsReleased) {
                  setIsAllocatableReadOnly(false);
                }
              }
            }}
          />
          <EditableField<AssetLocation, boolean>
            testId="lane-location-arrivals-switch"
            inputType={EditableFieldTypes.switch}
            label={t("input.labels.arrivals")}
            onProvide={(data) => data.arrivals}
            onChange={(data, value) => {
              data.arrivals = value;
              if (value == true) {
                data.blockPark = false;

                // When Block Park is set to false, re-enable the isAllocatable switch
                if (isExtraAllocatableLocationsReleased) {
                  setIsAllocatableReadOnly(false);
                }
              }
            }}
          />
        </>
      )}

    {isMeetAndGreetTypeEnabled && [AssetLocationTypes.Lane].includes(location.type) && (
      <EditableField<AssetLocation, boolean>
        testId="lane-location-meet-and-greet-switch"
        inputType={EditableFieldTypes.switch}
        label={t("input.labels.meetAndGreetPoint")}
        onProvide={(data) => (data.isCollectionPoint ?? false) || (data.isDeliveryPoint ?? false)}
        onChange={(data, value) => {
          data.isCollectionPoint = value;
          data.isDeliveryPoint = value;
        }}
      />
    )}

    {[AssetLocationTypes.Lane].includes(location.type) && (
      <EditableField<AssetLocation, boolean>
        testId="lane-location-block-park-switch"
        inputType={EditableFieldTypes.switch}
        label={t("input.labels.blockPark")}
        onProvide={(data) => data.blockPark}
        onChange={(data, value) => {
          data.blockPark = value;

          if (isExtraAllocatableLocationsReleased) {
            // When Block Park is set to true, set isAllocatable to true and disable its switch
            if (value === true) {
              data.isAllocatable = true;
              setIsAllocatableReadOnly(true);

              // When Block Park is set to false, re-enable the isAllocatable switch
            } else {
              setIsAllocatableReadOnly(false);
            }
          }

          if (value == true) {
            data.returns = false;
            data.arrivals = false;
          }
        }}
      />
    )}

    {isExtraAllocatableLocationsReleased && allocationSystem !== LaneAllocationSystem.None && location.type === AssetLocationTypes.Lane && (
      <EditableField<AssetLocation, boolean>
        testId="lane-location-can-be-allocated-switch"
        inputType={EditableFieldTypes.switch}
        label={t("input.labels.canBeAllocated")}
        readOnly={isAllocatableReadOnly}
        onProvide={(data) => {

          // When set to Block Park, the control should be read-only
          if (data.blockPark) {
            setIsAllocatableReadOnly(true);
          }

          // isAllocatable should be set to true if the location is Block Park, or customizable if not
          if (data.blockPark === true || data.isAllocatable === true) {
            return true;
          }

          return false;
        }}
        onChange={(data, value) => {
          data.isAllocatable = value;
        }}
      />
    )}

    <EditableField<AssetLocation, boolean>
      testId="lane-location-alert-overfilled-switch"
      inputType={EditableFieldTypes.switch}
      label={t("input.labels.alertIfOverfilled")}
      onProvide={(data) => data.alertIfOverfilled ?? false}
      onChange={(data, value) => {
        data.alertIfOverfilled = value;
      }}
    />


    {([AssetLocationTypes.DynamicBox].includes(location.type) &&
      <EditableField<AssetLocation, boolean>
        testId="lane-location-first-key-switch"
        inputType={EditableFieldTypes.switch}
        label={t("input.labels.firstKey")}
        onProvide={(data) => data.noMixedLaneAllocations ?? true}
        onChange={(data, value) => {
          data.noMixedLaneAllocations = value;
        }}
      />
    )}

    {([AssetLocationTypes.Lane, AssetLocationTypes.Zone].includes(location.type) && isCarWashLocationEnabled) && (
      <EditableField<AssetLocation, boolean>
        testId="lane-location-car-wash-switch"
        inputType={EditableFieldTypes.switch}
        label={t("input.labels.carWash")}
        onProvide={(data) => data.carWash}
        onChange={(data, value) => {
          data.carWash = value;
          // TODO: If this is a zone, cascade the setting to child lanes
          if (data.type === AssetLocationTypes.Zone && value) {
            // API call to update child lanes
            //updateChildLanes(data.id, value);
          }
        }}
      />
    )}

    {([AssetLocationTypes.Lane, AssetLocationTypes.Zone].includes(location.type) && isVipLocationEnabled) && (
      <EditableField<AssetLocation, boolean>
        testId="lane-location-vip-switch"
        inputType={EditableFieldTypes.switch}
        label={t("booking.labels.vip")}
        onProvide={(data) => data.vip}
        onChange={(data, value) => {
          data.vip = value;
        }}
      />
    )}

    {vehicleMoveKmosRulesEnabled && (
      <EditableField<AssetLocation, IDropDownListItem>
        testId="lane-location-vehicles-not-allocated-field"
        inputType={EditableFieldTypes.terminalField}
        alwaysVisible={!isRbacLocationsManagementReleased}
        label={t("input.labels.vehiclesNotAllocated")}
        onProvide={(data) => {
          return {
            name: MovementRuleAction[data.vehicleMovementRuleAction],
            value: data.vehicleMovementRuleAction?.toString() || ""
          };
        }}
        onChange={(data, item) => {
          if (item.value) {
            data.vehicleMovementRuleAction = parseInt(item.value) as MovementRuleAction
          }
        }}
        onRequestData={async () => (
          Object.values(MovementRuleAction)
            .filter(val => typeof val === 'string') as Array<KRT>)
          .map((key) => {
            return {
              name: key,
              value: MovementRuleAction[key].toString()
            }
          }
          )}
      />
    )}

    {(parentControlsKeyManagement != true && location.keyType != KeyManagementTypes.inherit && ([AssetLocationTypes.Zone, AssetLocationTypes.Lane].includes(location.type))) && (
      <>
        <div style={{ "width": "100%", marginTop: 20, marginBottom: 20 }}>
          <b>{t("locations.keyManagementOptions")}</b>
        </div>
        <EditableField<AssetLocation, boolean>
          testId="lane-location-auto-move-vehicle-switch"
          inputType={EditableFieldTypes.switch}
          label={t("input.labels.autoMoveVehicle")}
          onProvide={(data) => data.isAutoMoveLocation == true}
          onChange={(data, value) => {
            data.isAutoMoveLocation = value == true;
          }}
        />
        <EditableField<AssetLocation, boolean>
          testId="lane-location-accept-keys-switch"
          inputType={EditableFieldTypes.switch}
          label={t("input.labels.acceptKeys")}
          onChange={(data: AssetLocation, value: boolean) => data.isRestrictedToVehicleLocation = Boolean(value)}
          onProvide={(data: AssetLocation) => Boolean(data.isRestrictedToVehicleLocation)}
        />
        {!location.isRestrictedToVehicleLocation && (
          <>
            <EditableField<AssetLocation, IDropDownListItem>
              testId="lane-location-keys-not-allocated-field"
              inputType={EditableFieldTypes.terminalField}
              alwaysVisible={!isRbacLocationsManagementReleased}
              label={t("input.labels.keysNotAllocated")}
              onProvide={(data) => {
                return {
                  name: MovementRuleAction[data.keyscanRestriction],
                  value: data.keyscanRestriction?.toString() || ""
                };
              }}
              onChange={(data, item) => {
                if (item.value) {
                  data.keyscanRestriction = parseInt(item.value) as MovementRuleAction
                }
              }}
              onRequestData={async () => (
                Object.values(MovementRuleAction)
                  .filter(val => typeof val === 'string') as Array<KRT>)
                .map((key) => {
                  return {
                    name: key,
                    value: MovementRuleAction[key].toString()
                  }
                }
                )}
            />
          </>
        )}
      </>
    )}

  </Grid>)
}
